import { CONFIG_NUMBERS_SEPARATOR } from '@/libs/constants/configs'

/**
 * Formats a number as a quantity with specified minimum integer digits
 * @param number - The number to format
 * @param minIntDigits - Minimum number of integer digits
 * @returns Formatted quantity string
 */
export const formatQuantity = (
  number: string | number,
  minIntDigits = 2,
): string | number => {
  const isFormattedNumber = /^\d+$/.test(String(number))
  if (number && isFormattedNumber) {
    const result = Number(number)
    return result.toLocaleString('en-US', {
      minimumIntegerDigits: minIntDigits,
      useGrouping: false,
    })
  }
  return number
}

/**
 * Formats a price with currency symbol and number formatting based on locale
 * @param price - The price value to format
 * @param currencyUnit - The currency code (e.g., 'USD', 'EUR', 'GBP')
 * @param numberSeparator - Locale for number formatting
 * @param isCurrencyFront - Whether to display currency symbol in front of the price
 * @param currencySymbol - Optional custom currency symbol to use instead of the default
 * @returns Formatted price string
 */
export const formatPrice = (
  price: number,
  currencyUnit: string,
  numberSeparator: string = CONFIG_NUMBERS_SEPARATOR[1].value,
  isCurrencyFront = true,
  currencySymbol = '',
): string | number => {
  if (typeof price !== 'number' || !currencyUnit) {
    return price
  }

  const convertedPrice = Number(price)
  let currency = currencySymbol
  let formattedPrice = ''

  // Todo: Workaround for invalid currency
  // Improve later
  try {
    const priceParts = Intl.NumberFormat(numberSeparator, {
      style: 'currency',
      currency: currencyUnit,
    }).formatToParts(convertedPrice)

    for (const value of priceParts) {
      if (value.type === 'currency') {
        if (!currencySymbol) {
          currency = value.value
        }
        continue
      }
      if (value.type !== 'literal') {
        formattedPrice += value.value
      }
    }
  } catch {
    formattedPrice = Intl.NumberFormat(numberSeparator, {
      minimumFractionDigits: 2,
    }).format(convertedPrice)
  }

  if (!currency) {
    return formattedPrice
  }

  if (isCurrencyFront) {
    return `${currency} ${formattedPrice}`
  }

  return `${formattedPrice} ${currency}`
}
